<template>
  <aside class="tw-flex-col tw-min-h-0 tw-bg-white">
    <div class="tw-p-3 tw-pb-2">
      <div class="tw-relative">
        <i class="fe-search tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none"></i>
        <b-form-input v-model="search" type="search" debounce="400" class="tw-pl-9"
                      :placeholder="$t('chat.searchPlaceholder')"
                      autocomplete="off" data-lpignore="true" data-1p-ignore data-bwignore data-form-type="other"
        />
      </div>
    </div>

    <div class="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-flex tw-flex-col tw-px-2 tw-pb-2">
      <div v-if="!isSearching && recentChats === null" class="tw-flex tw-flex-col tw-gap-1 tw-pt-1">
        <div v-for="n in 4" :key="n" class="tw-flex tw-items-center tw-gap-3 tw-px-2.5 tw-py-2 tw-animate-pulse">
          <span class="tw-w-9 tw-h-9 tw-rounded-full tw-bg-gray-100 tw-flex-shrink-0"></span>
          <span class="tw-flex-1">
            <span class="tw-block tw-h-2.5 tw-w-2/3 tw-rounded tw-bg-gray-100"></span>
            <span class="tw-block tw-h-2 tw-w-1/2 tw-mt-2 tw-rounded tw-bg-gray-100"></span>
          </span>
        </div>
      </div>

      <template v-else-if="rows.length">
        <div class="tw-px-2.5 tw-pt-1 tw-pb-1.5 tw-text-xs tw-font-semibold tw-text-gray-500">
          {{ isSearching ? $t('chat.results') : $t('chat.recent') }}
        </div>
        <button v-for="row in rows" :key="row.id" type="button" data-testid="chat-row"
                class="chat-row tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-2.5 tw-py-2 tw-mb-0.5 tw-rounded-xl tw-border-0 tw-text-left tw-cursor-pointer"
                :class="selectedUser === row.id ? 'tw-bg-primary/5 tw-ring-1 tw-ring-inset tw-ring-primary' : 'tw-bg-transparent hover:tw-ring-1 hover:tw-ring-inset hover:tw-ring-primary-muted'"
                @click="select(row)"
        >
          <person-avatar :person="row.person" size="md"/>
          <span class="tw-flex-1 tw-min-w-0">
            <span class="tw-block tw-truncate tw-text-sm"
                  :class="row.isUnread ? 'tw-font-bold tw-text-gray-900' : 'tw-font-medium tw-text-gray-800'"
            >{{ row.person.firstName }} {{ row.person.lastName }}</span>
            <span v-if="row.preview" class="tw-block tw-truncate tw-text-xs tw-mt-0.5"
                  :class="row.isUnread ? 'tw-font-semibold tw-text-gray-800' : 'tw-text-gray-500'"
            >{{ row.preview }}</span>
          </span>
          <span v-if="row.isUnread" data-testid="chat-unread" class="tw-w-2 tw-h-2 tw-rounded-full tw-bg-primary tw-flex-shrink-0"
                :aria-label="$t('chat.unread')"
          ></span>
        </button>
      </template>

      <empty-state v-else-if="isSearching && !searchLoading" compact icon="fe-search" class="tw-my-auto"
                   :title="$t('chat.empty.noMatch.title')" :subtitle="$t('chat.empty.noMatch.subtitle')"
      />
      <empty-state v-else-if="!isSearching" compact icon="fe-message-circle" class="tw-my-auto"
                   :title="$t('chat.empty.recent.title')" :subtitle="$t('chat.empty.recent.subtitle')"
      />
    </div>
  </aside>
</template>

<script>
import {mapState} from "vuex";
import PersonAvatar from "@components/global/PersonAvatar.vue";
import {showChatRead, useRecentChats} from "@src/queries/chat";

export default {
  components: {PersonAvatar},
  setup() {
    const {chats} = useRecentChats();
    return {recentChats: chats};
  },
  data() {
    return {
      search: '',
      users: [],
      searchLoading: false
    }
  },
  computed: {
    ...mapState({
      selectedUser: state => state.chat.selectedUser,
    }),
    isSearching() {
      return this.search.trim().length > 0;
    },
    rows() {
      if (this.isSearching) {
        return this.users.map(u => ({id: u.mobileClientId, person: u, preview: u.email, isUnread: false}));
      }
      return (this.recentChats || []).filter(c => c.user)
          .map(c => ({id: c.user.id, person: c.user, preview: c.text, isUnread: c.isUnread}));
    }
  },
  watch: {
    search() {
      this.fetchUsersList();
    }
  },
  methods: {
    fetchUsersList() {
      if (!this.isSearching) {
        this.users = [];
        return;
      }
      this.searchLoading = true;
      this.$axios.get('/mobile/chat/user-list', {params: {search: this.search.trim()}}).then(res => {
        this.users = res.data;
      }).catch(() => {
        this.users = [];
      }).finally(() => {
        this.searchLoading = false;
      });
    },

    select(row) {
      const {firstName, lastName, image, email} = row.person;
      this.$store.dispatch('chat/setSelectedUserInfo', {
        id: row.id, firstName, lastName, image, email,
        fullName: `${firstName} ${lastName}`
      });
      this.$store.dispatch('chat/setSelectedUser', row.id);
      showChatRead(row.id);
    }
  }
}
</script>
